import { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Typography } from 'antd';
import { ExperimentOutlined, InfoCircleOutlined, RobotOutlined } from '@ant-design/icons';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { SectionCard } from '@/components/SectionCard';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { ScenarioResultSummary } from '@/api/validationRuns';
import {
  RESULT_STATE_LABEL,
  formatMs,
  formatUsd,
  type ResultState,
  type ResultStateName,
  type RoleUsage,
  type RunReading,
  type RunWorld,
} from '@/api/runEvidence';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// What a run's page leads with (SI-03b, screen 07 frame A). Before any count it says WHAT was tested: the agent, the
// candidate revision and the deployment that actually answered. Then the five states, counted once per library
// scenario — Needs attention, Evidence incomplete, Not assessed, Passed applicable checks — with drafts apart as an
// Exploratory filter, never a fifth state. Results are grouped worst first; a zero stays on screen. Cost is by role,
// "Recorded priced usage: $X, plus N unpriced calls"; time comes with its sample sizes. Every word is the server's.
// ─────────────────────────────────────────────────────────────────────────────

type Lens = ResultStateName | 'Exploratory' | 'All';

const ORDER: ResultStateName[] = ['NeedsAttention', 'AwaitingReview', 'EvidenceIncomplete', 'NotAssessed', 'Passed'];
export const STATE_TONE: Record<ResultStateName, PillTone> = { NeedsAttention: 'red', AwaitingReview: 'ink', EvidenceIncomplete: 'amber', NotAssessed: 'slate', Passed: 'green' };
export const STATE_ACCENT: Record<ResultStateName, string> = {
  NeedsAttention: colors.red[600],
  AwaitingReview: colors.ink[600],
  EvidenceIncomplete: colors.amber[600],
  NotAssessed: colors.slate[400],
  Passed: colors.green[600],
};
const GROUP_HINT: Record<ResultStateName, string> = {
  NeedsAttention: 'a check failed on a turn that ran',
  AwaitingReview: 'every automatic check passed; an obligation a reviewer judges waits for one',
  EvidenceIncomplete: 'not a pass: something a check needed was not captured',
  NotAssessed: 'the run did not complete the scenario, or nothing was checked; not the agent’s failure',
  Passed: 'every check that applied passed',
};
const ROLE_LABEL: Record<string, string> = { Agent: 'Agent', Peer: 'Peers it consulted', Router: 'Skill router', Other: 'Other calls' };
const PASSED_SHOWN = 4;

/** World seeder W5: the evaluation worlds the run's results ran in, each with what it was and how it ended. */
function WorldsCard({ worlds }: { worlds: RunWorld[] }) {
  return (
    <SectionCard title="Evaluation worlds" subtitle="Set up for this run on reserved patients of practice 100, and removed after it.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="run-worlds">
        {worlds.map((w) => (
          <div key={w.worldId}>
            <Text strong>{w.words}</Text> <Text type="secondary" style={{ fontFamily: MONO, fontSize: 12 }}>· {w.role}</Text>
            <div style={{ fontSize: 12.5 }}>
              Built for {w.practiceDate.slice(0, 10)} on reserved patient #{w.memberId}{w.memberProtocolId ? `, course #${w.memberProtocolId}` : ''}.{' '}
              {w.state === 'TornDown' ? 'Removed after the run.' : 'Still held: it is removed when its lease runs out.'}
            </div>
            <div style={{ fontSize: 12.5 }}>
              <Text type="secondary">Ran in it: {w.scenarioIds.join(', ')}</Text>
            </div>
            {w.problem && <Text type="warning" style={{ display: 'block', fontSize: 12.5 }}>{w.problem}</Text>}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export const RunReadingPanel = observer(function RunReadingPanel({
  reading,
  results,
  onOpen,
}: {
  reading: RunReading;
  results: ScenarioResultSummary[];
  onOpen: (resultId: number) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [lens, setLens] = useState<Lens>('All');
  const [showAllPassed, setShowAllPassed] = useState(false);
  const byId = useMemo(() => new Map(results.map((r) => [r.id, r])), [results]);
  const s = reading.summary;

  const library = reading.results.filter((r) => !r.exploratory);
  const drafts = reading.results.filter((r) => r.exploratory);
  const counts: Record<ResultStateName, number> = {
    NeedsAttention: s.needsAttention,
    AwaitingReview: s.awaitingReview ?? 0,
    EvidenceIncomplete: s.evidenceIncomplete,
    NotAssessed: s.notAssessed,
    Passed: s.passed,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <IdentityStrip reading={reading} isDark={isDark} />

      {reading.notes.length > 0 && (
        <Alert type="info" showIcon icon={<InfoCircleOutlined />} title="What this run's record cannot show" description={<Notes notes={reading.notes} />} />
      )}

      {(reading.worlds ?? []).length > 0 && <WorldsCard worlds={reading.worlds ?? []} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Filter results by state">
        {ORDER.map((state) => (
          <LensChip
            key={state}
            label={RESULT_STATE_LABEL[state]}
            count={counts[state]}
            color={STATE_ACCENT[state]}
            dashed={counts[state] === 0}
            active={lens === state}
            onClick={() => setLens(lens === state ? 'All' : state)}
            isDark={isDark}
          />
        ))}
        <LensChip
          label="Exploratory drafts"
          count={s.exploratory}
          color={colors.violet[600]}
          dashed={s.exploratory === 0}
          active={lens === 'Exploratory'}
          onClick={() => setLens(lens === 'Exploratory' ? 'All' : 'Exploratory')}
          isDark={isDark}
        />
      </div>
      <Text type="secondary" style={{ fontSize: 12, marginTop: -6 }} data-testid="states-sentence">
        The five states count the {s.libraryScenarios} library scenario{s.libraryScenarios === 1 ? '' : 's'} in this run, each
        once ({ORDER.map((state) => counts[state]).join(' + ')} = {s.libraryScenarios}).
        {' '}Exploratory is a filter, not a state: {s.exploratory === 0
          ? 'no draft ran.'
          : `${s.exploratory} draft${s.exploratory === 1 ? '' : 's'} (${s.exploratoryPassed} passed, ${s.exploratoryNeedsAttention} need attention) are shown apart and never count toward release.`}
      </Text>

      <div className="si-run-columns" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 300px)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {ORDER.filter((state) => lens === 'All' || lens === state).map((state) => {
            const rows = library.filter((r) => r.state === state);
            if (rows.length === 0) return null;
            const limited = state === 'Passed' && !showAllPassed && lens === 'All' ? rows.slice(0, PASSED_SHOWN) : rows;
            return (
              <div key={state} data-testid={`group-${state}`}>
                <GroupHead tone={STATE_TONE[state]} label={RESULT_STATE_LABEL[state]} hint={GROUP_HINT[state]} count={rows.length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {limited.map((r) => (
                    <ResultRow key={r.resultId} state={r} summary={byId.get(r.resultId)} accent={STATE_ACCENT[r.state]} onOpen={onOpen} isDark={isDark} />
                  ))}
                </div>
                {limited.length < rows.length && (
                  <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setShowAllPassed(true)}>
                    Show all {rows.length}
                  </Button>
                )}
              </div>
            );
          })}
          {(lens === 'All' || lens === 'Exploratory') && drafts.length > 0 && (
            <div data-testid="group-Exploratory">
              <GroupHead tone="violet" label="Exploratory" hint="drafts that ran · cannot satisfy a release gate" count={drafts.length} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {drafts.map((r) => (
                  <ResultRow key={r.resultId} state={r} summary={byId.get(r.resultId)} accent={colors.violet[600]} onOpen={onOpen} isDark={isDark} exploratory />
                ))}
              </div>
            </div>
          )}
          {reading.results.length === 0 && <Text type="secondary">This run recorded no result.</Text>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <CostCard reading={reading} isDark={isDark} />
          <TimeCard reading={reading} isDark={isDark} />
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .si-run-columns { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
    </div>
  );
});

function IdentityStrip({ reading, isDark }: { reading: RunReading; isDark: boolean }) {
  const id = reading.identity;
  const u = reading.usage;
  const answered = u.roles.find((r) => r.role === 'Agent');
  const cell = (label: string, value: React.ReactNode, sub?: React.ReactNode, warn?: boolean) => (
    <div style={{ minWidth: 0, padding: '10px 14px', borderLeft: `3px solid ${warn ? colors.red[600] : 'transparent'}` }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.slate[500] }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: 13.5, color: isDark ? colors.slate[100] : colors.slate[900], wordBreak: 'break-word' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 2 }}>{sub}</div>}
    </div>
  );
  return (
    <SectionCard noPadding>
      <div
        data-testid="run-identity"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 0 }}
      >
        {cell(
          'Agent',
          <span>
            <RobotOutlined /> {id.agentCanonicalName ?? id.agentName ?? `Agent #${id.agentId}`}
          </span>,
          [id.agentName, `#${id.agentId}`].filter(Boolean).join(' · '),
        )}
        {cell(
          'Candidate revision',
          <span style={{ fontFamily: MONO }}>{id.candidateHash ? id.candidateHash.slice(0, 7) : 'Not recorded'}</span>,
          id.candidateHash ? 'The configuration read once before the first scenario' : 'This run predates candidate manifests',
        )}
        {cell(
          'Actual deployment',
          answered && answered.deployments.length > 0
            ? answered.deployments.map((d) => d.deployment ?? 'unrecorded').join(', ') + (id.apiTitle ? ` · ${id.apiTitle}` : '')
            : 'Not recorded',
          u.deploymentCheck,
          u.deploymentMatched === false,
        )}
        {cell('Evaluator', 'No semantic judge ran', 'Every check in this run is deterministic; the Evaluator setting has no consumer yet (D-SI-7)')}
      </div>
      {id.merchantId != null && (
        <div style={{ padding: '0 14px 10px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <StatusPill tone="slate">Mode: real agent, isolated</StatusPill>
          <StatusPill tone="slate">Practice #{id.merchantId}, captured at submit</StatusPill>
          <StatusPill tone="slate">Tools that change or send: answered by a double</StatusPill>
        </div>
      )}
    </SectionCard>
  );
}

/** One state as a filter chip with its count; a zero stays on screen, dashed. Shared with the Scenario Intelligence Overview. */
export function LensChip({
  label,
  count,
  color,
  dashed,
  active,
  onClick,
  isDark,
}: {
  label: string;
  count: number;
  color: string;
  dashed: boolean;
  active: boolean;
  onClick: () => void;
  isDark: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        cursor: 'pointer',
        border: `1.5px ${dashed ? 'dashed' : 'solid'} ${dashed ? (isDark ? colors.slate[600] : colors.slate[300]) : color}`,
        background: active ? (isDark ? `${color}33` : `${color}14`) : isDark ? colors.slate[800] : '#FFFFFF',
        color: dashed ? colors.slate[500] : color,
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      {label}
      <span
        style={{
          minWidth: 20,
          padding: '0 6px',
          borderRadius: 999,
          background: dashed ? 'transparent' : `${color}22`,
          textAlign: 'center',
          fontSize: 12,
        }}
      >
        {count}
      </span>
    </button>
  );
}

/** A group's head: the state's pill, its count and one line on what it means. Shared with the Scenario Intelligence Overview. */
export function GroupHead({ tone, label, hint, count }: { tone: PillTone; label: string; hint: string; count: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      <StatusPill tone={tone}>{label}</StatusPill>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {count} · {hint}
      </Text>
    </div>
  );
}

function ResultRow({
  state,
  summary,
  accent,
  onOpen,
  isDark,
  exploratory,
}: {
  state: ResultState;
  summary?: ScenarioResultSummary;
  accent: string;
  onOpen: (resultId: number) => void;
  isDark: boolean;
  exploratory?: boolean;
}) {
  const facts = [
    summary ? `${summary.turnCount} turn${summary.turnCount === 1 ? '' : 's'}` : null,
    summary?.elapsedMs ? formatMs(summary.elapsedMs) : null,
    exploratory ? 'draft · never counts toward release' : null,
  ].filter(Boolean);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 10,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `4px solid ${accent}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 12.5, wordBreak: 'break-all' }}>{state.scenarioId}</div>
        <div style={{ fontSize: 13, marginTop: 2 }}>{state.reason}</div>
        {facts.length > 0 && (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {facts.join(' · ')}
          </Text>
        )}
      </div>
      <Button
        size="small"
        type={state.state === 'NeedsAttention' && !exploratory ? 'primary' : 'default'}
        icon={exploratory ? <ExperimentOutlined /> : undefined}
        onClick={() => onOpen(state.resultId)}
      >
        {state.state === 'Passed' ? 'Open' : 'Open finding'}
      </Button>
    </div>
  );
}

function CostCard({ reading, isDark }: { reading: RunReading; isDark: boolean }) {
  const u = reading.usage;
  const border = isDark ? colors.slate[700] : colors.slate[200];
  return (
    <SectionCard title="Cost of this run">
      <div data-testid="run-cost-by-role">
        {u.roles.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            {u.tracedTurns === 0 ? 'Not recorded: this run predates per-turn traces. Price unavailable, not $0.' : 'No model call was recorded.'}
          </Text>
        ) : (
          u.roles.map((r) => <RoleRow key={r.role} r={r} border={border} />)
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, paddingTop: 10 }}>
          <Text strong>Recorded priced usage</Text>
          <Text strong>{u.pricedCostUsd != null ? formatUsd(u.pricedCostUsd) : '—'}</Text>
        </div>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {u.unpricedCalls > 0
            ? `Plus ${u.unpricedCalls} unpriced call${u.unpricedCalls === 1 ? '' : 's'} (${u.roles.filter((r) => r.unpricedCalls > 0).map((r) => ROLE_LABEL[r.role] ?? r.role).join(', ')}): counted, never added as $0.`
            : 'Every recorded call was priced.'}
          {u.tracedTurns < u.turns && u.tracedTurns > 0 ? ` ${u.turns - u.tracedTurns} of ${u.turns} turns are not counted (no trace).` : ''}
        </Text>
      </div>
    </SectionCard>
  );
}

function RoleRow({ r, border }: { r: RoleUsage; border: string }) {
  const tokens = `${r.inputTokens.toLocaleString()} input${r.cachedTokens ? ` (${r.cachedTokens.toLocaleString()} cached)` : ''} + ${r.outputTokens.toLocaleString()} output tokens · ${r.calls} call${r.calls === 1 ? '' : 's'}`;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: `1px solid ${border}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {ROLE_LABEL[r.role] ?? r.role} · {r.deployments.map((d) => d.deployment ?? 'unrecorded').join(', ')}
        </div>
        <Text type="secondary" style={{ fontSize: 11.5 }}>{tokens}</Text>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        {r.pricedCostUsd != null ? <Text strong>{formatUsd(r.pricedCostUsd)}</Text> : <StatusPill tone="slate">Price unavailable</StatusPill>}
        {r.pricedCostUsd != null && r.unpricedCalls > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>+ {r.unpricedCalls} unpriced</Text>
          </div>
        )}
      </div>
    </div>
  );
}

function TimeCard({ reading }: { reading: RunReading; isDark: boolean }) {
  const t = reading.timing;
  const row = (label: string, value: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 0' }}>
      <Text type="secondary" style={{ fontSize: 12.5 }}>{label}</Text>
      <Text style={{ fontSize: 12.5, textAlign: 'right' }}>{value}</Text>
    </div>
  );
  return (
    <SectionCard title="Time">
      {row('End to end', t.endToEndMs != null ? formatMs(t.endToEndMs) : 'Not recorded')}
      {row('Per scenario', t.medianScenarioMs != null ? `median ${formatMs(t.medianScenarioMs)} (n = ${t.scenarios})` : 'Not recorded')}
      {row('Model call', t.medianModelCallMs != null ? `median ${formatMs(t.medianModelCallMs)} (n = ${t.modelCalls})` : 'Not recorded')}
      <Text type="secondary" style={{ fontSize: 11 }}>Model-call time and end-to-end time are never mixed.</Text>
    </SectionCard>
  );
}

function Notes({ notes }: { notes: string[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {notes.map((n) => (
        <li key={n}>{n}</li>
      ))}
    </ul>
  );
}
